import { useMemo } from "react";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { makeUrl, routes } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import { DonutChart } from "../DonutChart";
import { type StatusSegment } from "../StatusBreakdownChart";
import { Text } from "../Text";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import { spaceColorMap } from "./monitoringPanelLayout";
import {
  foldsPersonal,
  PERSONAL_BUCKET,
  PERSONAL_BUCKET_COLOR,
} from "./monitoringScope";
import { useMonitoringScope } from "./useMonitoringScope";
import {
  useMonitoringSpaces,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

type SpaceRow = {
  space: DisplayableSpace;
  label: string;
  count: number;
  workflowNames: string[];
};

export function ActiveWorkflowsBySpace() {
  const recordInteraction = useMonitoringInteraction();
  const { report, loading } = useMonitoringWorkflows();
  const { spaces, loading: spacesLoading } = useMonitoringSpaces();
  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.workflows
  );
  const displayName = useSpaceDisplayName();

  const workflows = useMemo(
    () => report.workflows.filter((workflow) => workflow.active),
    [report]
  );

  const rows = useMemo(() => {
    const bySpace = new Map<string, string[]>();
    for (const workflow of workflows) {
      const names = bySpace.get(workflow.spaceId) ?? [];
      names.push(workflow.name);
      bySpace.set(workflow.spaceId, names);
    }

    const result: SpaceRow[] = [];
    for (const space of spaces) {
      if (!matchesSpace(space.id)) continue;
      const workflowNames = bySpace.get(space.id) ?? [];
      result.push({
        space,
        label: displayName(space),
        count: workflowNames.length,
        workflowNames,
      });
    }

    result.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    return result;
  }, [workflows, spaces, matchesSpace, displayName]);

  const colorMap = useMemo(() => spaceColorMap(spaces), [spaces]);

  const segments = useMemo<StatusSegment[]>(() => {
    const out: StatusSegment[] = [];
    let personalCount = 0;
    const personalItems: string[] = [];
    for (const row of rows) {
      if (row.count === 0) continue;
      if (foldsPersonal(scope) && personalIds.has(row.space.id)) {
        personalCount += row.count;
        personalItems.push(...row.workflowNames);
        continue;
      }
      out.push({
        key: row.space.id,
        count: row.count,
        color: colorMap.get(row.space.id)!,
        label: row.label,
        items: row.workflowNames,
      });
    }
    if (personalCount > 0) {
      out.push({
        key: PERSONAL_BUCKET,
        count: personalCount,
        color: PERSONAL_BUCKET_COLOR,
        label: "Personal",
        items: personalItems,
      });
    }
    return out;
  }, [rows, colorMap, scope, personalIds]);

  const total = useMemo(
    () => workflows.filter((workflow) => matchesSpace(workflow.spaceId)).length,
    [workflows, matchesSpace]
  );

  if (loading || spacesLoading) {
    return <PanelSkeleton />;
  }

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>Last 30 days</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Active workflows by space</Text>
        <TotalRow>
          <Text variant="display-md">{total}</Text>
        </TotalRow>
      </PanelHeader>
      <DonutChart
        segments={segments}
        total={total}
        placeholder={total === 0}
        strokeWidth={6}
        centerCaption="workflows"
        scrollableLegend
        segmentHref={(s) =>
          s.key === PERSONAL_BUCKET
            ? undefined
            : makeUrl(routes.space, { spaceId: s.key })
        }
        onSegmentNavigate={(s) =>
          recordInteraction("workflows.space_link_click", {
            targetType: "space",
            targetId: s.key,
          })
        }
      />
    </Panel>
  );
}
